import {z} from 'zod';

import {getAgeFromDateOfBirth, isValidDateOfBirth} from '../../../utils/date';
import {PHONE_NUMBER_REGEX_2} from '../../../utils/validation';

const REQUIRED_FIELD_MESSAGE = 'This is a required field';

// Email validation
export const emailSchema = z
  .string({message: REQUIRED_FIELD_MESSAGE})
  .min(1, {message: 'Email address is required'})
  .email({message: 'Please enter a valid email address'});

// Password validation (basic - min 8 chars)
export const passwordSchema = z
  .string({message: REQUIRED_FIELD_MESSAGE})
  .min(1, {message: 'A password is required'})
  .min(8, {message: 'Password must be at least 8 characters'});

// Password validation (strong - with complexity requirements)
export const strongPasswordSchema = z
  .string({message: 'Please enter a password'})
  .min(8, {message: 'Password must be at least 8 characters'})
  .regex(/[A-Z]/, {message: 'Password must contain at least one uppercase letter'})
  .regex(/[a-z]/, {message: 'Password must contain at least one lowercase letter'})
  .regex(/[0-9]/, {message: 'Password must contain at least one number'})
  .regex(/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/, {
    message: 'Password must contain at least one symbol',
  });

// Verification code (6 digits)
export const verificationCodeSchema = z
  .string({message: REQUIRED_FIELD_MESSAGE})
  .length(6, {message: 'Verification must be 6 characters'});

// Login schema
export const loginSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
});

export type LoginFormData = z.infer<typeof loginSchema>;

// Account entry schema (email only)
export const accountEntrySchema = z.object({
  email: emailSchema,
});

export type AccountEntryFormData = z.infer<typeof accountEntrySchema>;

// Verification schema
export const verificationSchema = z.object({
  verification: verificationCodeSchema,
});

export type VerificationFormData = z.infer<typeof verificationSchema>;

// OTP Verification form schema (alias for rewards verification screens)
export const otpVerificationSchema = verificationSchema;
export type OtpVerificationFormData = VerificationFormData;

// Password reset schema (with optional bypass)
export const passwordResetSchema = z
  .object({
    password: z.string(),
    passwordValidationBypass: z.boolean().default(false),
  })
  .superRefine((data, ctx) => {
    // If bypass is false, apply strong validation
    if (!data.passwordValidationBypass) {
      const result = strongPasswordSchema.safeParse(data.password);
      if (!result.success) {
        result.error.issues.forEach(issue => {
          ctx.addIssue({
            code: 'custom',
            path: ['password'],
            message: issue.message,
          });
        });
      }
    } else {
      // If bypass is true, only check minimum length
      const result = passwordSchema.safeParse(data.password);
      if (!result.success) {
        result.error.issues.forEach(issue => {
          ctx.addIssue({
            code: 'custom',
            path: ['password'],
            message: issue.message,
          });
        });
      }
    }
  });

export type PasswordResetFormData = z.infer<typeof passwordResetSchema>;

// Account password change schema (password + confirm + bypass, no billing address)
// Used for CHANGE_PASSWORD action in AccountCreateScreen
export const accountPasswordChangeSchema = z
  .object({
    password: z.string(),
    confirmPassword: z.string({message: REQUIRED_FIELD_MESSAGE}),
    passwordValidationBypass: z.boolean().default(false),
  })
  .superRefine((data, ctx) => {
    // Password strength validation (conditional based on bypass)
    if (!data.passwordValidationBypass) {
      const result = strongPasswordSchema.safeParse(data.password);
      if (!result.success) {
        result.error.issues.forEach(issue => {
          ctx.addIssue({
            code: 'custom',
            path: ['password'],
            message: issue.message,
          });
        });
      }
    } else {
      // If bypass is true, only check minimum length
      const result = passwordSchema.safeParse(data.password);
      if (!result.success) {
        result.error.issues.forEach(issue => {
          ctx.addIssue({
            code: 'custom',
            path: ['password'],
            message: issue.message,
          });
        });
      }
    }

    // Password matching validation
    if (data.password !== data.confirmPassword) {
      ctx.addIssue({
        code: 'custom',
        path: ['confirmPassword'],
        message: "Passwords don't match",
      });
    }
  });

export type AccountPasswordChangeFormData = z.infer<typeof accountPasswordChangeSchema>;

// Password change schema (current + new + confirm)
export const passwordChangeSchema = z
  .object({
    currentPassword: passwordSchema,
    newPassword: passwordSchema,
    confirmPassword: z.string({message: REQUIRED_FIELD_MESSAGE}),
  })
  .refine(data => data.newPassword === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

export type PasswordChangeFormData = z.infer<typeof passwordChangeSchema>;

// Simple password confirmation schema (used in AccountPasswordResetScreen)
export const passwordConfirmationSchema = z
  .object({
    password: passwordSchema,
    confirmPassword: z.string({message: REQUIRED_FIELD_MESSAGE}),
  })
  .refine(data => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

export type PasswordConfirmationFormData = z.infer<typeof passwordConfirmationSchema>;

// === Australian Phone Number Schema ===

const PHONE_NUMBER_REGEX = /^(\+?61|0)[2-478](?:[ -]?[0-9]){8}$/;

export const phoneNumberSchema = z
  .string({message: REQUIRED_FIELD_MESSAGE})
  .regex(PHONE_NUMBER_REGEX, {message: 'Please enter a valid Australian phone number'})
  .nullable();

// Phone number schema for rewards sign-in (matches original SignInSchema regex)
const rewardsPhoneNumberSchema = z
  .string({message: REQUIRED_FIELD_MESSAGE})
  .min(1, {message: REQUIRED_FIELD_MESSAGE})
  .regex(PHONE_NUMBER_REGEX_2, {message: 'Please enter a valid Australian phone number'})
  .nullable();

// Phone number only schema (for rewards sign-in)
export const phoneNumberOnlySchema = z.object({
  phoneNumber: rewardsPhoneNumberSchema,
});

export type PhoneNumberOnlyFormData = z.infer<typeof phoneNumberOnlySchema>;

// === Date of Birth Schema (18+ validation) ===
// Format: YYYY-MM-DD (matches DateOfBirthInput component output and utils/date.ts)

export const dateOfBirthSchema = z
  .string({message: REQUIRED_FIELD_MESSAGE})
  .regex(/^\d{4}-\d{2}-\d{2}$/, {message: 'Invalid date'}) // Enforce YYYY-MM-DD format
  .refine(
    dateString => {
      if (!dateString) return false;
      return isValidDateOfBirth(dateString);
    },
    {message: 'Invalid date'},
  )
  .refine(
    dateString => {
      if (!dateString) return false;
      const age = getAgeFromDateOfBirth(dateString);
      return age >= 18;
    },
    {message: 'You must be 18 or older to create an account'},
  )
  .nullable();

// === Suburb/Address Schema ===

export const suburbSchema = z
  .object({
    id: z.string().optional(),
    locality: z
      .string()
      .transform(str => str?.trim())
      .pipe(z.string().min(1, 'Locality is required'))
      .optional(),
    postal_code: z
      .string()
      .transform(str => str?.trim())
      .pipe(z.string().min(1, 'Postal code is required'))
      .optional(),
    administrative_area: z
      .string()
      .transform(str => str?.trim())
      .pipe(z.string().min(1, 'Administrative area is required'))
      .optional(),
  })
  .refine(data => data.locality && data.postal_code && data.administrative_area, {
    message: 'Please select a suburb',
  })
  .nullable();

// === Rewards Sign Up Schema ===

export const rewardsSignUpSchema = z.object({
  firstName: z.string({message: REQUIRED_FIELD_MESSAGE}).min(1, REQUIRED_FIELD_MESSAGE).nullable(),
  lastName: z.string({message: REQUIRED_FIELD_MESSAGE}).min(1, REQUIRED_FIELD_MESSAGE).nullable(),
  email: emailSchema,
  dateOfBirth: dateOfBirthSchema,
  phoneNumber: phoneNumberSchema,
  suburb: suburbSchema,
  signUpCode: z.string().nullable().optional(),
  confirmCheckbox: z
    .boolean({message: REQUIRED_FIELD_MESSAGE})
    .refine(val => val === true, {message: 'This field must be checked'}),
});

export type RewardsSignUpFormData = z.infer<typeof rewardsSignUpSchema>;

// === Billing Address Schema ===

export const billingAddressSchema = z
  .object({
    lines: z
      .array(
        z
          .string()
          .transform(str => str.trim())
          .pipe(z.string().min(1, 'Address line cannot be empty')),
      )
      .min(1, 'At least one address line is required'),
    locality: z
      .string()
      .transform(str => str.trim())
      .pipe(z.string().min(2, 'Suburb is required')),
    administrative_area: z
      .string({message: REQUIRED_FIELD_MESSAGE})
      .transform(str => str.trim())
      .pipe(z.string().min(1, REQUIRED_FIELD_MESSAGE)),
    postal_code: z
      .string({message: 'Postcode is required'})
      .transform(str => str.trim())
      .pipe(z.string().min(1, 'Postcode is required')),
  })
  .nullable();

// === Account Creation Base Schema (without passwords) ===

export const accountCreateBaseSchema = z.object({
  firstName: z.string({message: REQUIRED_FIELD_MESSAGE}).min(1, REQUIRED_FIELD_MESSAGE).nullable(),
  lastName: z.string({message: REQUIRED_FIELD_MESSAGE}).min(1, REQUIRED_FIELD_MESSAGE).nullable(),
  email: emailSchema,
  dateOfBirth: dateOfBirthSchema,
  phoneNumber: phoneNumberSchema,
  billingAddress: billingAddressSchema.refine(val => val !== null, {
    message: 'A billing address is required',
  }),
  passwordValidationBypass: z.boolean().default(false),
  optInRewards: z.boolean().default(false),
  signUpCode: z.string().nullable().optional(),
});

// === Account Creation with Passwords Schema ===

export const accountCreateWithPasswordSchema = accountCreateBaseSchema
  .extend({
    password: passwordSchema,
    confirmPassword: z.string({message: REQUIRED_FIELD_MESSAGE}),
  })
  .refine(data => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

// === Account Creation/Change Password with Strong Validation + Bypass ===

export const accountCreatePasswordSchema = accountCreateBaseSchema
  .extend({
    password: z.string(),
    confirmPassword: z
      .string({message: REQUIRED_FIELD_MESSAGE})
      .min(1, {message: 'This is a required field'}),
    passwordValidationBypass: z.boolean().default(false),
  })
  .superRefine((data, ctx) => {
    // Password strength validation (conditional based on bypass)
    if (!data.passwordValidationBypass) {
      const result = strongPasswordSchema.safeParse(data.password);
      if (!result.success) {
        result.error.issues.forEach(issue => {
          ctx.addIssue({
            code: 'custom',
            path: ['password'],
            message: issue.message,
          });
        });
      }
    } else {
      // If bypass is true, only check minimum length
      const result = passwordSchema.safeParse(data.password);
      if (!result.success) {
        result.error.issues.forEach(issue => {
          ctx.addIssue({
            code: 'custom',
            path: ['password'],
            message: issue.message,
          });
        });
      }
    }

    // Password matching validation (always enforced)
    if (data.password !== data.confirmPassword) {
      ctx.addIssue({
        code: 'custom',
        path: ['confirmPassword'],
        message: "Passwords don't match",
      });
    }
  });

export type AccountCreateBaseFormData = z.infer<typeof accountCreateBaseSchema>;
export type AccountCreateWithPasswordFormData = z.infer<typeof accountCreateWithPasswordSchema>;
export type AccountCreatePasswordFormData = z.infer<typeof accountCreatePasswordSchema>;

// === Rewards Full Sign Up Schema ===

export const rewardsFullSignUpBaseSchema = z
  .object({
    password: passwordSchema,
    confirmPassword: z.string({message: REQUIRED_FIELD_MESSAGE}),
    billingAddress: billingAddressSchema.refine(val => val !== null, {
      message: 'A billing address is required',
    }),
    passwordValidationBypass: z.boolean().default(false),
  })
  .refine(data => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

// Variant with required passwordValidationBypass checkbox (for weak password flow)
export const rewardsFullSignUpWithBypassSchema = z
  .object({
    password: passwordSchema,
    confirmPassword: z.string({message: REQUIRED_FIELD_MESSAGE}),
    billingAddress: billingAddressSchema.refine(val => val !== null, {
      message: 'A billing address is required',
    }),
    passwordValidationBypass: z
      .boolean({message: REQUIRED_FIELD_MESSAGE})
      .refine(val => val === true, {message: 'This is a required field'}),
  })
  .refine(data => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

export type RewardsFullSignUpFormData = z.infer<typeof rewardsFullSignUpBaseSchema>;
export type RewardsFullSignUpWithBypassFormData = z.infer<typeof rewardsFullSignUpWithBypassSchema>;

export {REQUIRED_FIELD_MESSAGE};
